import type { Dispatch, SetStateAction } from "react";
import type { Topic } from "@/app/(main)/forum/_components/types";
import SearchHorizontalContentCard from "./_components/search-horizontal-content-card";
import SearchTopicCard from "./_components/search-topic-card";
import type { Author } from "@/types/content";
import type { ContentCard } from "@/types/dashboard";

export interface SearchResultContent {
  id: string;
  type: number;
  title: string;
  slug: string;
  lead?: string;
  summary?: string;
  thumbnailUrl?: string;
  coverImageUrl?: string;
  publishedAt?: string;
  publishedLabel?: string;
  likeCount?: number;
  commentCount?: number;
  viewCount?: number;
  isLiked?: boolean;
  isSaved?: boolean;
  author?: Author;
  categoryNames?: string[];
  categories?: {
    id: string;
    name: string;
    slug: string;
  }[];
}

export type SearchResultItem = SearchResultContent | Topic;

// Keep the old name exported as an alias just in case, but we prefer SearchResultItem
export type SearchResult = SearchResultItem;

interface SearchItemsProps {
  results: SearchResultItem[];
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const mapToContentCard = (result: SearchResultContent): ContentCard => {
  return {
    author: result.author || {
      avatar: "",
      bio: "",
      id: "",
      isFollowing: false,
      isVerified: false,
      name: "",
      socialMediaLinks: [],
      title: "",
    },
    category: result.categories?.[0]
      ? {
          id: result.categories[0].id,
          name: result.categories[0].name,
          parent: null,
          slug: result.categories[0].slug,
        }
      : {
          id: "",
          name: result.categoryNames?.[0] || "",
          parent: null,
          slug: "",
        },
    commentCount: result.commentCount || 0,
    coverImageUrl: result.coverImageUrl,
    durationLabel: "",
    id: result.id,
    isBookmarked: result.isSaved || false,
    isCommented: false,
    isLiked: result.isLiked || false,
    lead: result.lead || result.summary || "",
    likeCount: result.likeCount || 0,
    publishedAt: result.publishedAt || "",
    publishedLabel: result.publishedLabel || "",
    slug: result.slug,
    summary: result.summary,
    thumbnailUrl: result.thumbnailUrl,
    title: result.title,
    type: result.type,
    viewCount: result.viewCount || 0,
  };
};

export function SearchItems({ results, setIsOpen }: SearchItemsProps) {
  if (results.length === 0) {
    return (
      <div className="text-center py-12 text-neutral-500">
        Sonuç bulunamadı.
      </div>
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Event delegation to handle link clicks
    // biome-ignore lint/a11y/useKeyWithClickEvents: Event delegation to handle link clicks
    <div
      className="flex flex-col gap-6 md:gap-8 py-8"
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest("a")) {
          setIsOpen(false);
        }
      }}
    >
      {results.map((result) => {
        if ("type" in result && result.type !== undefined) {
          const contentCardProps = mapToContentCard(
            result as SearchResultContent,
          );
          return (
            <div
              className="border-b border-neutral-100 pb-8 last:border-none last:pb-0"
              key={result.id}
            >
              <SearchHorizontalContentCard content={contentCardProps} />
            </div>
          );
        }

        return (
          <div
            className="border-b border-neutral-100 pb-8 last:border-none last:pb-0"
            key={result.id}
          >
            <SearchTopicCard topic={result as Topic} />
          </div>
        );
      })}
    </div>
  );
}
